import S, { DataSignal } from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import * as _ from 'underscore';
import { IAjax } from 'lib/Ajax';
import { AutoSavingDraftObject } from 'lib/autoSave';
import UOM from 'lib/UOM';
import { LatinLower } from 'lib/diacriticals';
import { ValidationWatcher } from 'lib/validationWatcher';
import nonuniqueIndex from 'lib/nonuniqueIndex';

import { Berth } from 'data/models/Berth';
import { BerthAmenityType } from 'data/models/BerthAmenityType';
import { BerthAmenity, BerthAmenityData } from 'data/models/BerthAmenity';
import { BerthCapability } from 'data/models/BerthCapability';
import { BerthCapabilityAssignment, BerthCapabilityAssignmentData } from 'data/models/BerthCapabilityAssignment';
import { BerthProfileResponseData } from 'data/models/BerthProfileResponse';
import { CompassPoint, CompassPointType } from 'server/enums/CompassPoint';
import { PointsOfType, Antipode, Abbreviation as CompassAbbreviation } from 'data/models/CompassPointExtensions';
import { DomainSettings } from 'data/models/DomainSettings';
import { Location } from 'data/models/Location';
import { Measurement } from 'data/models/Measurement';
import { PierGroup } from 'data/models/PierGroup';
import { PostalAddress } from 'data/models/PostalAddress';
import { SetPierGroupFootmarksRequest, SetBerthFootmarksRequest } from 'data/models/SetPierGroupFootmarksRequest';
import { parseZoneCoordinates, LocationCoords, CircleCoords, LatLong, ZONE_TYPE_CIRCLE } from 'data/models/ZoneCoordinates';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';
import { uomIsFeet } from 'common/services/uomIsFeet';

import { PierGroupFootmarks, PierGroupFootmarksParams } from 'portcall/piergroupfootmarks/model';
import { PostalAddress as PostalAddressViewModel } from 'portcall/postaladdress/model';
import { ToggleableMeasurement } from 'portcall/toggleablemeasurement/model';

import { BerthProfileCache } from './cache';

export interface BerthProfileParams {
    id : number;
    editName : boolean;
}

export type BerthProfile = ReturnType<typeof BerthProfile>;
export function BerthProfile(
    ajax : IAjax,
    entrypoints : Entrypoints,
    domainSettings : DomainSettings,
    data : PCCache,
    cache : BerthProfileCache,
    postalAddressModel : (addr : PostalAddress) => PostalAddressViewModel,
    pgFootmarksModel : (params : PierGroupFootmarksParams) => PierGroupFootmarks,
    { id, editName } : BerthProfileParams
) {
    const model = cache.BerthProfileResponses.update1(null, { BerthId: id }),
        url = entrypoints.BerthProfile({ Id: id }),
        request = ajax.Url<BerthProfileResponseData>(url).Get(),
        autoEditName = S.data(editName), // should the editName query string param be shown -- will set to false once the user blurs the name once
        autoEditNextAmenity = S.data(false),
        autoEditNextCapability = S.data(false);

    // Mini dependency injection kernel
    const
        amenityModel = (amenity : BerthAmenity) => AmenityViewModel(
            amenity,
            autoEditNextAmenity,
            model.Ref.CanEdit,
            ajax,
            data
        ),
        capabilityAssignmentModel = (cap : BerthCapabilityAssignment) => CapabilityAssignmentViewModel(
            cap,
            autoEditNextCapability,
            model.Ref.CanEdit,
            ajax,
            data
        ),
        amenitiesModel = (berth : Berth) => AmenitiesViewModel(
            berth,
            model.Ref.AmenityTypes,
            ajax,
            data,
            amenityModel
        ),
        capabilitiesModel = (berth : Berth) => CapabilitiesViewModel(
            berth,
            model.Ref.Capabilities,
            ajax,
            data,
            capabilityAssignmentModel
        ),
        zoneOperationsModel = (loc : Location, coords : () => LocationCoords, onCloseModal : () => void, params : ZoneOperationsParams) => ZoneOperationsViewModel(
            loc,
            coords,
            onCloseModal,
            params,
            domainSettings
        ),
        locationModel = (loc : Location) => LocationViewModel(
            loc,
            autoEditName,
            model.Ref.CanEdit,
            ajax,
            zoneOperationsModel
        ),
        pgModel = (pg : PierGroup, mc : MeasurementsCore) => PierGroupViewModel(
            pg,
            ajax,
            pg => PierGroupMeasurementsViewModel(pg, mc),
            (pg, onCloseModal) => PierGroupFootmarksPopup(pg, mc, onCloseModal, ajax, entrypoints, pgFootmarksModel),
            ConfigureChangeWindowPopup
        ),
        berthModel = (berth : Berth) => BerthViewModel(
            berth,
            ajax,
            data,
            MeasurementsCore,
            BerthMeasurementsViewModel,
            pgModel,
            locationModel,
            postalAddressModel,
            CompassPointsViewModel,
            amenitiesModel,
            capabilitiesModel
        );

    const berth = S(() => model.Berth() && berthModel(model.Berth()!));

    S(() => {
        // Hide editName query string param once Name is flipped to non-editable
        if (autoEditName() && berth()?.Location() && !berth()!.Location()!.EditingName()) {
            autoEditName(false);
        }
    });

    request.done(data => {
        cache.BerthProfileResponses.update1(request.Date, data);
    });

    return {
        Id: id,
        Model: model,
        Berth: berth,
        Organizations: lift(() => Array.from(data.Organizations.items()))
            .filter(o => o.Active())
            .orderBy(o => LatinLower(o.Name())),
        AutoEditName: autoEditName,
        DomainSettings: domainSettings,
        GoBack: function () {
            window.history.back();
        }
    };
}

export type BerthViewModel = ReturnType<typeof BerthViewModel>;
function BerthViewModel(
    berth : Berth,
    ajax : IAjax,
    data : PCCache,
    measurementsCoreModel : (berth : Berth) => MeasurementsCore,
    berthMeasurementsModel : (berth : Berth, mc : MeasurementsCore) => BerthMeasurementsViewModel,
    pierGroupModel : (pg : PierGroup, mc : MeasurementsCore) => PierGroupViewModel,
    locationModel : (loc : Location) => LocationViewModel,
    postalAddressModel : (addr : PostalAddress) => PostalAddressViewModel,
    compassPointsModel : (berth : Berth) => CompassPointsViewModel,
    amenitiesModel : (berth : Berth) => AmenitiesViewModel,
    capabilitiesModel : (berth : Berth) => CapabilitiesViewModel
) {
    const client = ajax.Url<Berth>(() => berth.Url() || '', { useCache: true }),
        editables = [
            'AutoCalculateBills',
            'BerthTransitOffset',
            'DaylightMooringOnly',
            'LengthBuffer.Num',
            'LengthBuffer.UOM',
            'MaxDraftAllowed.Num',
            'MaxDraftAllowed.UOM',
            'MultiVesselBerthing',
            'PortSideVesselHasBowToLowFootmark',
            'LowFootmarkCompassPoint',
            'Notes',
            'SchedulingAuthorityId',
            'TimeBuffer'
        ],
        draft = AutoSavingDraftObject(berth, client, editables),
        measurementsCore = measurementsCoreModel(draft),
        measurements = berthMeasurementsModel(draft, measurementsCore),
        pierGroup = S(() => berth.PierGroup() && pierGroupModel(berth.PierGroup()!, measurementsCore)),
        location = S(() => berth.Location() && locationModel(berth.Location()!)),
        postalAddress = S(() => berth.PostalAddress() && postalAddressModel(berth.PostalAddress()!)),
        compassPoints = compassPointsModel(draft),
        amenities = amenitiesModel(berth),
        capabilities = capabilitiesModel(berth),
        editing = {
            Address             : S.data(false),
            SchedulingAuthority : S.data(false)
        };

    draft.SchedulingAuthority = S.value(S.sample(berth.SchedulingAuthority));
    data.Berths.attach(draft, [draft.SchedulingAuthority]);

    return {
        Model: draft,
        PierGroup: pierGroup,
        Location: location,
        PostalAddress: postalAddress,
        CompassPoints: compassPoints,
        Amenities: amenities,
        Capabilities: capabilities,
        Editing: {
            Address: editing.Address,
            ToggleAddress: () => editing.Address(!editing.Address()),
            SchedulingAuthority: editing.SchedulingAuthority,
            ToggleSchedulingAuthority: () => editing.SchedulingAuthority(!editing.SchedulingAuthority())
        },
        Measurements: measurements
    };
}

export type PierGroupViewModel = ReturnType<typeof PierGroupViewModel>;
function PierGroupViewModel(
    pierGroup : PierGroup,
    ajax : IAjax,
    pgMeasurementsModel : (pg : PierGroup) => PierGroupMeasurementsViewModel,
    footmarksPopupModel : (pg : PierGroup, onCloseModal : () => void) => PierGroupFootmarksPopup,
    configureChangeWindowModel : (pg : PierGroup, onCloseModal : () => void) => ConfigureChangeWindowPopup
) {
    const client = ajax.Url<PierGroup>(() => pierGroup.Url() || ''),
        editables = [
            'PortId',
            'StartOverhang.Num',
            'StartOverhang.UOM',
            'EndOverhang.Num',
            'EndOverhang.UOM',
            'PreventAgentOverlaps',
            'RequireKnownVessels',
            'AgentChangeWindowHours'
        ],
        draft = AutoSavingDraftObject(pierGroup, client, editables),
        validation = ValidationWatcher(client),
        editing = {
            Port: S.data(false)
        },
        measurements = pgMeasurementsModel(draft),
        ShowEditFootmarks = S.value(false),
        EditFootmarks = S(() => ShowEditFootmarks() ?
            footmarksPopupModel(pierGroup, () => ShowEditFootmarks(false)) :
            null
        ),
        showConfigureChangeWindow = S.value(false),
        configureChangeWindowPopup = S(() => showConfigureChangeWindow() ?
            configureChangeWindowModel(draft, () => showConfigureChangeWindow(false)) :
            null
        );

    return {
        Model: draft,
        Validation: validation,
        Measurements: measurements,
        ShowEditFootmarks,
        EditFootmarks,
        ChangeWindowToggleChecked,
        NonNullChangeWindow,
        ConfigureChangeWindowPopup: configureChangeWindowPopup,
        Editing: {
            Port: editing.Port,
            TogglePort: () => editing.Port(!editing.Port())
        }
    };

    function ChangeWindowToggleChecked(checked? : boolean) {
        if (checked === undefined) {
            return draft.AgentChangeWindowHours() !== null || showConfigureChangeWindow();
        } else if (checked) {
            showConfigureChangeWindow(true);
            return true;
        } else {
            draft.AgentChangeWindowHours(null);
            return false;
        }
    }

    function NonNullChangeWindow(val? : number) {
        if (val === undefined) {
            return draft.AgentChangeWindowHours() || 0;
        } else {
            draft.AgentChangeWindowHours(val);
            return val;
        }
    }
}

export type LocationViewModel = ReturnType<typeof LocationViewModel>;
function LocationViewModel(
    location : Location,
    autoEditName : () => boolean,
    canEdit : () => boolean,
    ajax : IAjax,
    zoneOperationsModel : (loc : Location, coords : () => LocationCoords, onCloseModal : () => void, params : ZoneOperationsParams) => ZoneOperationsViewModel
) {
    const client = ajax.Url<Location>(() => location.Url() || ''),
        draft = AutoSavingDraftObject(location, client),
        validation = ValidationWatcher(client),
        editingName = S.data(S.sample(() => autoEditName() && canEdit())),
        coords = S(() => parseZoneCoordinates(draft.ZoneCoordinates(), draft.ZoneType())),
        zoneOperationsParams = S.data<ZoneOperationsParams | null>(null),
        zoneOperationsModal = S(() => zoneOperationsParams() && zoneOperationsModel(
            draft,
            coords,
            () => zoneOperationsParams(null),
            zoneOperationsParams()!
        ));

    return {
        Model: draft,
        Validation: validation,
        Coords: coords,
        EditingName: editingName,
        ToggleEditingName: () => editingName(!editingName()),
        ZoneOperationsParams: zoneOperationsParams,
        ZoneOperationsModal: zoneOperationsModal
    };
}

export interface ZoneOperationsParams {
    selectedZoneType : 'polygon' | 'circle';
    cancelLeafletEdit : () => void;
}

export const enum ZoneOperationsMode {
    PromptOverwrite,
    PromptManualEntry,
    PerformManualEntry
}

export type ZoneOperationsViewModel = ReturnType<typeof ZoneOperationsViewModel>;
function ZoneOperationsViewModel(
    location : Location,
    coords : () => LocationCoords,
    onCloseModal : () => void,
    { selectedZoneType, cancelLeafletEdit } : ZoneOperationsParams,
    domainSettings : DomainSettings
) {
    const mode = S.data(S.sample(location.ZoneCoordinates) ? ZoneOperationsMode.PromptOverwrite : ZoneOperationsMode.PromptManualEntry),
        errorMessage = S.data(''),
        // prepopulate from curr zone coordinates
        currCoords = S.sample(coords),
        latLong = currCoords ?
            currCoords instanceof CircleCoords ?
                currCoords.latLong :
                currCoords.coords.length > 0 ?
                    currCoords.coords[0] :
                    null :
            null,
        anchorageLatitude = S.data(latLong ? latLong.lat : 0),
        anchorageLongitude = S.data(latLong ? latLong.long : 0),
        anchorageRadius = new Measurement({
            Num: currCoords instanceof CircleCoords ? currCoords.radius : 0,
            UOM: UOM.Meter
        }),
        displayUom = S.data(S.sample(() => domainSettings.Pier.DefaultLengthUOM() || UOM.Foot)),
        anchorageIsFeet = uomIsFeet(displayUom),
        toggleableRadius = ToggleableMeasurement({
            backingMeasurement: anchorageRadius,
            uom: displayUom,
            conversions: [{
                uom: UOM.Foot,
                decimalPlaces: 0
            }, {
                uom: UOM.Meter,
                decimalPlaces: 1
            }]
        });

    return {
        Mode : mode,
        ConfirmOverwrite: ConfirmOverwrite,
        CancelLeafletEdit: CancelLeafletEdit,
        ContinueLeafletEdit: ContinueLeafletEdit,
        SaveManualEntry: SaveManualEntry,
        ErrorMessage: errorMessage,
        AnchorageLatitude: anchorageLatitude,
        AnchorageLongitude: anchorageLongitude,
        ToggleableRadius: toggleableRadius,
        AnchorageIsFeet: anchorageIsFeet
    };

    function ConfirmOverwrite() {
        // TH: For circle zones we allow "manual" entry via input boxes or entry via Leaflet Draw on map.
        // For polygon zones we only allow entry via Leaflet Draw on map.
        // Not sure why that distinction, but I'm preserving it for now...
        if (selectedZoneType === 'polygon') {
            onCloseModal(); // close modal to allow leaflet edit to continue...
        } else {
            mode(ZoneOperationsMode.PromptManualEntry); // move on to next step in modal...
        }
    }

    function CancelLeafletEdit() {
        cancelLeafletEdit();
        onCloseModal();
    }

    function ContinueLeafletEdit() {
        onCloseModal();
    }

    function SaveManualEntry() {
        if (anchorageLatitude() < -90 || anchorageLatitude() > 90) {
            errorMessage('Latitude entry is invalid');
        } else if (anchorageLongitude() < -180 || anchorageLongitude() > 180) {
            errorMessage('Longitude entry is invalid');
        } else {
            S.freeze(() => {
                location.ZoneType(ZONE_TYPE_CIRCLE);
                location.ZoneCoordinates(new CircleCoords(new LatLong(anchorageLatitude(), anchorageLongitude()), anchorageRadius.Num()).toString());
                CancelLeafletEdit();
            });
        }
    }
}

export type CompassPointsViewModel = ReturnType<typeof CompassPointsViewModel>;
function CompassPointsViewModel(berth : Berth) {
    const
        // Type of points to show in dropdown UI: N, NE, E, SE, etc.
        availablePoints = PointsOfType(CompassPointType.Cardinal | CompassPointType.Ordinal).map(compass => ({
            Id: compass,
            Abbreviation: CompassAbbreviation(compass)
        })),
        lowFootmarkCompassAbbreviation = () => CompassAbbreviation(berth.LowFootmarkCompassPoint()),
        highFootmarkCompassAbbreviation = () => CompassAbbreviation(Antipode(berth.LowFootmarkCompassPoint())),
        showCompassPointsMenu = S.data(false);

    return {
        AvailablePoints: availablePoints,
        LowFootmarkCompassAbbreviation: lowFootmarkCompassAbbreviation,
        HighFootmarkCompassAbbreviation: highFootmarkCompassAbbreviation,
        ShowCompassPointsMenu: showCompassPointsMenu,
        ToggleCompassPointsMenu: ToggleCompassPointsMenu,
        SelectCompassPoint: SelectCompassPoint
    };

    function ToggleCompassPointsMenu() {
        showCompassPointsMenu(!showCompassPointsMenu());
    }

    function SelectCompassPoint(compass : CompassPoint) {
        S.freeze(() => {
            berth.LowFootmarkCompassPoint(compass);
            showCompassPointsMenu(false);
        });
    }
}

export type AmenitiesViewModel = ReturnType<typeof AmenitiesViewModel>;
function AmenitiesViewModel(
    berth : Berth,
    amenityTypes : ArraySignal<BerthAmenityType>,
    ajax : IAjax,
    data : PCCache,
    amenityModel : (amenity : BerthAmenity) => AmenityViewModel
) {
    const client = ajax.Url<BerthAmenity, BerthAmenityData>(() => berth.AmenitiesUrl() || ''),
        assignedAmenitiesIdx = nonuniqueIndex(berth.Amenities, ba => ba.AmenityId()),
        availableAmenityTypes = amenityTypes.filter(bat =>
            bat.OrganizationId() === berth.SchedulingAuthorityId()
            && bat.Active() && !assignedAmenitiesIdx(bat.Id())().length),
        amenities = berth.Amenities.map(amenityModel);

    return {
        AvailableAmenityTypes: availableAmenityTypes,
        AvailableForSlot: AvailableForSlot,
        Amenities: amenities,
        Add: addAmenity
    };

    function addAmenity() {
        if (!availableAmenityTypes().length) return;
        let req = client.Post(new BerthAmenity({
            Id: 0,
            AmenityId: availableAmenityTypes()[0].Id()
        }));
        req.then(function (_data) {
            data.BerthAmenities.update1(req.Date, _data);
        });
    }

    function AvailableForSlot(amenity : AmenityViewModel) {
        const amenityType = amenity.Model.Amenity();
        return _.uniq((amenityType ? [amenityType] : []).concat(availableAmenityTypes()));
    }
}

export type AmenityViewModel = ReturnType<typeof AmenityViewModel>;
function AmenityViewModel(
    amenity : BerthAmenity,
    autoEditNextAmenity : DataSignal<boolean>,
    canEdit : () => boolean,
    ajax : IAjax,
    data : PCCache
) {
    const client = ajax.Url<BerthAmenity>(() => amenity.Url() || ''),
        draft = AutoSavingDraftObject(amenity, client),
        editing = S.data(S.sample(() => canEdit() && autoEditNextAmenity()));

    draft.Amenity = S.value(S.sample(amenity.Amenity));
    data.BerthAmenities.attach(draft, [draft.Amenity]);

    // TH: Set flag so that future amenities will be auto-editable upon creation.
    // This will take effect in the next tick, so it won't affect amenity view models that are constructed in this same tick.
    autoEditNextAmenity(true);

    return {
        Model: draft,
        Editing: editing,
        ToggleEditing: ToggleEditing,
        Delete: Delete
    };

    function ToggleEditing() {
        editing(!editing());
    }

    function Delete() {
        const key = amenity.Id();
        let req = client.Delete();
        req.then(function () {
            data.BerthAmenities.update(req.Date, [], [key]);
        });
    }
}

export type CapabilitiesViewModel = ReturnType<typeof CapabilitiesViewModel>;
function CapabilitiesViewModel(
    berth : Berth,
    capabilities : ArraySignal<BerthCapability>,
    ajax : IAjax,
    data : PCCache,
    capabilityAssignmentModel : (cap : BerthCapabilityAssignment) => CapabilityAssignmentViewModel
) {
    const client = ajax.Url<BerthCapabilityAssignment, BerthCapabilityAssignmentData>(() => berth.BerthCapabilityAssignmentsUrl() || ''),
        assignedCapabilitiesIdx = nonuniqueIndex(berth.Capabilities, bca => bca.CapabilityId()),
        availableCapabilities = capabilities.filter(bca =>
            bca.Active() && !assignedCapabilitiesIdx(bca.Id())().length),
        assignedCapabilities = berth.Capabilities.map(capabilityAssignmentModel);

    return {
        AvailableCapabilities: availableCapabilities,
        AvailableForSlot: AvailableForSlot,
        Capabilities: assignedCapabilities,
        Add: addCapability
    };

    function addCapability() {
        if (!availableCapabilities().length) return;
        let req = client.Post(new BerthCapabilityAssignment({
            Id: 0,
            CapabilityId: availableCapabilities()[0].Id()
        }));
        req.then(function (_data) {
            data.BerthCapabilityAssignments.update1(req.Date, _data);
        });
    }

    function AvailableForSlot(capAssignment : CapabilityAssignmentViewModel) {
        const cap = capAssignment.Model.Capability();
        return _.uniq((cap ? [cap] : []).concat(availableCapabilities()));
    }
}

export type CapabilityAssignmentViewModel = ReturnType<typeof CapabilityAssignmentViewModel>;
function CapabilityAssignmentViewModel(
    capabilityAssignment : BerthCapabilityAssignment,
    autoEditNextCapability : DataSignal<boolean>,
    canEdit : () => boolean,
    ajax : IAjax,
    data : PCCache
) {
    const client = ajax.Url<BerthCapabilityAssignment>(() => capabilityAssignment.Url() || ''),
        draft = AutoSavingDraftObject(capabilityAssignment, client),
        editing = S.data(S.sample(() => canEdit() && autoEditNextCapability()));

    draft.Capability = S.value(S.sample(capabilityAssignment.Capability));
    data.BerthCapabilityAssignments.attach(draft, [draft.Capability]);

    // TH: Set flag so that future capabilities will be auto-editable upon creation.
    // This will take effect in the next tick, so it won't affect view models that are constructed in this same tick.
    autoEditNextCapability(true);

    return {
        Model: draft,
        Editing: editing,
        ToggleEditing: ToggleEditing,
        Delete: Delete
    };

    function ToggleEditing() {
        editing(!editing());
    }

    function Delete() {
        const key = capabilityAssignment.Id(),
            req = client.Delete();
        req.then(function () {
            data.BerthCapabilityAssignments.update(req.Date, [], [key]);
        });
    }
}

type MeasurementsCore = ReturnType<typeof MeasurementsCore>;
function MeasurementsCore(berth : Berth) {
    const uom = S.data(S.sample(berth.Length.UOM)),
        // TH: TODO: Review these conversions... Do we want feet and inches for the draft?
        lengthConversions = [{
            uom: UOM.Foot,
            decimalPlaces: 0
        }, {
            uom: UOM.Meter,
            decimalPlaces: 1
        }],
        depthConversions = [{
            uom: UOM.Foot,
            decimalPlaces: 1
        }, {
            uom: UOM.Meter,
            decimalPlaces: 2
        }];

    return { uom, lengthConversions, depthConversions };
}

export type BerthMeasurementsViewModel = ReturnType<typeof BerthMeasurementsViewModel>;
function BerthMeasurementsViewModel(berth : Berth, { uom, lengthConversions, depthConversions } : MeasurementsCore) {
    const
        endMark = berth.StartMark.add(berth.Length),
        measurementsInFeet = uomIsFeet(uom),
        toggleableLength = ToggleableMeasurement({ backingMeasurement: berth.Length, uom, conversions: lengthConversions }),
        toggleableLengthBuffer = ToggleableMeasurement({ backingMeasurement: berth.LengthBuffer, uom, conversions: lengthConversions }),
        toggleableMaxDraft = ToggleableMeasurement({ backingMeasurement: berth.MaxDraftAllowed, uom, conversions: depthConversions }),
        toggleableStartMark = ToggleableMeasurement({ backingMeasurement: berth.StartMark, uom, conversions: lengthConversions }),
        toggleableEndMark = ToggleableMeasurement({ backingMeasurement: endMark, uom, conversions: lengthConversions });

    return {
        MeasurementsInFeet: measurementsInFeet,
        ToggleableLength: toggleableLength,
        ToggleableLengthBuffer: toggleableLengthBuffer,
        ToggleableMaxDraft: toggleableMaxDraft,
        ToggleableStartMark: toggleableStartMark,
        ToggleableEndMark: toggleableEndMark
    };
}

export type PierGroupMeasurementsViewModel = ReturnType<typeof PierGroupMeasurementsViewModel>;
function PierGroupMeasurementsViewModel(
    pierGroup : PierGroup,
    { uom, lengthConversions } : MeasurementsCore
) {
    const
        toggleableStartOverhang = ToggleableMeasurement({ backingMeasurement: pierGroup.StartOverhang, uom, conversions: lengthConversions }),
        toggleableEndOverhang = ToggleableMeasurement({ backingMeasurement: pierGroup.EndOverhang, uom, conversions: lengthConversions });

    return {
        ToggleableStartOverhang: toggleableStartOverhang,
        ToggleableEndOverhang: toggleableEndOverhang
    };
}

export type PierGroupFootmarksPopup = ReturnType<typeof PierGroupFootmarksPopup>;
function PierGroupFootmarksPopup(
    pierGroup : PierGroup,
    { uom: displayUOM } : MeasurementsCore,
    onCloseModal : () => void,
    ajax : IAjax,
    entrypoints : Entrypoints,
    pgFootmarksModel : (params : PierGroupFootmarksParams) => PierGroupFootmarks
) {
    const
        url = () => entrypoints.PierGroupFootmarks({ Id: pierGroup.Id() }),
        client = ajax.Url<SetPierGroupFootmarksRequest, unknown>(url, {
            include: [r => r.Berths],
            useCache: true
        }),
        req = new SetPierGroupFootmarksRequest(),
        Footmarks = pgFootmarksModel({
            req,
            pierLengthUOM: () => pierGroup.Port()?.PierLengthUOM() ?? null,
            displayUOM
        }),
        Saving = S.value(false);

    // Initialize req.Berths based on current piergroup berth arrangement. Users can then edit
    // from there.
    S.sample(() => {
        const berths = pierGroup.Berths().slice();
        // The PierGroupFootmarks component assumes the order of the passed-in berths represents
        // the desired StartMark order, so sort by StartMark before passing berths into the
        // component.
        berths.sort(Berth.StartMarkOrder);
        req.Berths(berths.map(b => new SetBerthFootmarksRequest({
            Id: b.Id(),
            StartMark: {
                Num: b.StartMark.Num(),
                UOM: b.StartMark.UOM()
            },
            Length: {
                Num: b.Length.Num(),
                UOM: b.Length.UOM()
            }
        })));
    });

    return {
        Footmarks,
        CanSave: () => Footmarks.FootmarksAreValid(),
        Save,
        Saving,
        Cancel: onCloseModal
    };

    function Save() {
        if (Saving()) return;
        Saving(true);
        client.Post(req).then(() => onCloseModal()).finally(() => Saving(false));
    }
}

const DEFAULT_CHANGE_WINDOW_HOURS = 6;

export type ConfigureChangeWindowPopup = ReturnType<typeof ConfigureChangeWindowPopup>;
function ConfigureChangeWindowPopup(pg : PierGroup, onCloseModal : () => void) {
    const ChangeWindowHours = S.value(DEFAULT_CHANGE_WINDOW_HOURS);

    return {
        ChangeWindowHours,
        Cancel: onCloseModal,
        Commit
    };

    function Commit() {
        S.freeze(() => {
            pg.AgentChangeWindowHours(ChangeWindowHours());
            onCloseModal();
        });
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/berthprofile/model.ts